import { useState } from "react"
import { MarkdownEditor } from "./features/markdown-editor"
import { Button } from "./ui/button"
import { Loader2, ArrowLeft } from "lucide-react"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { GripVertical } from "lucide-react"
import { Header } from "./layout/header"

const sampleContent = `# Test Document

This is a test document with lots of content to force scrolling.

## Section 1

Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.

- Item 1
- Item 2
- Item 3
- Item 4
- Item 5

## Section 2

More content here. We need enough content to exceed the viewport height and trigger scrolling. Lorem ipsum dolor sit amet, consectetur adipiscing elit.

### Subsection 2.1

Even more content to make sure we have enough to scroll. This should definitely exceed the viewport.

### Subsection 2.2

Additional subsection with more text to ensure we have plenty of scrollable content.

## Section 3

1. Numbered item 1
2. Numbered item 2
3. Numbered item 3
4. Numbered item 4
5. Numbered item 5

## Section 4

Code example:

\`\`\`javascript
function hello() {
  console.log("Hello world");
  return true;
}

function goodbye() {
  console.log("Goodbye world");
  return false;
}
\`\`\`

## Section 5

More text here. Keep adding content to make it scrollable. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.

### Subsection 5.1

Even more content in a subsection.

## Section 6

Even more content. We want to make absolutely sure there's enough content to scroll. This is a very important section with critical information.

### Subsection 6.1

Additional content to force scrolling.

### Subsection 6.2

More and more content.

## Section 7

Final section with more text to ensure scrolling works properly. This should be at the bottom of the document.

### Subsection 7.1

Almost done!

### Subsection 7.2

The very last section.

## Section 8

One more section just to be absolutely sure we have enough content.

## Section 9

And another one!

## Section 10

Finally, the last section with some concluding thoughts and final notes.

`

export function TestEditor() {
  const [content, setContent] = useState(sampleContent)
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const handleChange = (newContent: string) => {
    setContent(newContent)
    setHasUnsavedChanges(true)
  }

  const handleSave = () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      setLastSaved(new Date())
      setHasUnsavedChanges(false)
      console.log('Saved:', content.substring(0, 100))
    }, 500)
  }

  return (
    <div className="h-screen w-full flex flex-col overflow-hidden bg-background">
      {/* Breadcrumb */}
      <div className="border-b p-3 bg-sidebar">
        <a
          href="/?test=index"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Prototype Index</span>
        </a>
      </div>
      {/* Header like the real app */}
      <Header onSearchClick={() => console.log('Search clicked')} />

      {/* Use PanelGroup like the real app */}
      <PanelGroup direction="horizontal" className="flex-1">
        {/* Left Sidebar Panel */}
        <Panel defaultSize={20} minSize={15} maxSize={30}>
          <div className="h-full border-r border-border bg-sidebar flex flex-col">
            <div className="p-3 border-b border-border">
              <h2 className="text-sm font-semibold">Projects</h2>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-3 space-y-2">
                <div className="text-xs text-muted-foreground">Test Sidebar</div>
                <div className="text-xs text-muted-foreground">Scrollable area</div>
                {Array.from({ length: 50 }).map((_, i) => (
                  <div key={i} className="text-xs py-1">Item {i + 1}</div>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-px bg-border hover:bg-primary transition-colors relative group">
          <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </PanelResizeHandle>

        {/* Main Content Panel (EXACT structure from resizable-layout + document-viewer) */}
        <Panel defaultSize={55} minSize={30}>
          <div className="h-full flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-background/50">
            <h1 className="text-lg font-semibold">Test Document</h1>
          </div>

          {/* Document Content Wrapper (from document-viewer line 382) */}
          <div className="flex-1 overflow-hidden">
            {/* Single view wrapper (from document-viewer line 384) */}
            <div className="h-full overflow-hidden">
              {/* DocumentContent component structure (from line 588) */}
              <div className="h-full flex flex-col">
                {/* Editor (EXACT structure from document-viewer line 627) */}
                <div className="flex-1 overflow-hidden min-h-0">
                  <MarkdownEditor
                    initialContent={content}
                    onChange={handleChange}
                    darkMode={false}
                    className="h-full"
                  />
                </div>

                {/* Save Status Bar - Moved to bottom */}
                <div className="flex items-center justify-between px-4 py-1 border-t border-border bg-muted/30 text-xs">
                  <div className="flex items-center gap-2">
                    {saving && (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span className="text-muted-foreground">Saving...</span>
                      </>
                    )}
                    {!saving && lastSaved && !hasUnsavedChanges && (
                      <span className="text-muted-foreground">
                        Saved {lastSaved.toLocaleTimeString()}
                      </span>
                    )}
                    {hasUnsavedChanges && !saving && (
                      <span className="text-orange-600">Unsaved changes</span>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    disabled={saving || !hasUnsavedChanges}
                    className="h-7 px-3"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          </div>
        </Panel>

        <PanelResizeHandle className="w-px bg-border hover:bg-primary transition-colors relative group">
          <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        </PanelResizeHandle>

        {/* Right Sidebar Panel */}
        <Panel defaultSize={25} minSize={20} maxSize={40}>
          <div className="h-full border-l border-border bg-card flex flex-col">
            <div className="p-3 border-b border-border">
              <h2 className="text-sm font-semibold">AI Assistant</h2>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-3 space-y-2">
                <div className="text-xs text-muted-foreground">Test Chat</div>
                {Array.from({ length: 30 }).map((_, i) => (
                  <div key={i} className="text-xs py-1">Message {i + 1}</div>
                ))}
              </div>
            </div>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  )
}

